import { supabase } from '@/lib/integrations/supabase'
import { validateCapability } from '@/lib/permissions/permissions'
import type { Role } from '@/lib/permissions/types'
import type {
  CreateEntryInput,
  FinancialEntry,
  ListEntriesFilters,
  UpdateEntryInput,
} from './entriesTypes'
export async function listEntriesForParty(
  unitId: string,
  partyId: string,
): Promise<FinancialEntry[]> {
  const { data, error } = await supabase
    .from('financial_entries')
    .select('*')
    .eq('unit_id', unitId)
    .eq('party_id', partyId)
    .order('due_date', { ascending: false })
  if (error) throw error
  return (data as FinancialEntry[]) || []
}


/** Returns true when an entry is past due but not yet paid or canceled. */
export function isOverdue(entry: FinancialEntry): boolean {
  return entry.status === 'open' && entry.due_date < new Date().toISOString().split('T')[0]
}

export async function listEntries(
  unitId: string,
  filters?: ListEntriesFilters,
): Promise<FinancialEntry[]> {
  let query = supabase
    .from('financial_entries')
    .select('*')
    .eq('unit_id', unitId)
    .order('due_date', { ascending: true })

  if (filters?.entry_type)  query = query.eq('entry_type', filters.entry_type)
  if (filters?.status)      query = query.in('status', filters.status)
  if (filters?.dueDateFrom) query = query.gte('due_date', filters.dueDateFrom)
  if (filters?.dueDateTo)   query = query.lte('due_date', filters.dueDateTo)

  const { data, error } = await query
  if (error) throw error
  
  const entries = (data as FinancialEntry[]) || []
// Get unique party IDs and fetch current pix_key from parties table
  const partyIds = [...new Set(entries.map(e => e.party_id).filter(Boolean))]
  if (partyIds.length > 0) {
    try {
      const { data: parties, error: partiesError } = await supabase
        .from('parties')
        .select('id, pix_key')
        .in('id', partyIds)
      
      if (!partiesError && parties) {
        const pixKeyMap = new Map(parties.map((p: any) => [p.id, p.pix_key]))
        // Update party_pix_key_snapshot with current pix_key from parties table
        entries.forEach(entry => {
          if (entry.party_id && pixKeyMap.has(entry.party_id)) {
            const val = pixKeyMap.get(entry.party_id)
            entry.party_pix_key_snapshot = val == null ? null : String(val)
          }
        })
      }
    } catch (err) {
      console.warn('[listEntries] Error fetching current pix keys:', err)
    }
  }
  
  return entries
}

export async function createEntry(input: CreateEntryInput, userRole?: Role): Promise<FinancialEntry> {
  if (userRole) {
    await validateCapability(userRole, 'canEditEntries')
  }
  const { data, error } = await supabase
    .from('financial_entries')
    .insert(input)
    .select()
    .single()
  if (error) throw error
  return data as FinancialEntry
}

export async function updateEntry(id: string, input: UpdateEntryInput, userRole?: Role): Promise<FinancialEntry> {
  if (userRole) {
    await validateCapability(userRole, 'canEditEntries')
  }
  const { data, error } = await supabase
    .from('financial_entries')
    .update(input)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data as FinancialEntry
}

export async function deleteEntry(id: string, userRole?: Role): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canDeleteEntries')
  }
  const { error } = await supabase
    .from('financial_entries')
    .delete()
    .eq('id', id)
  if (error) throw error
}

export async function markAsPaid(id: string, paidAt?: string, userRole?: Role): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canEditEntries')
  }
  const paidAtValue = paidAt
    ? (() => { const [y, m, d] = paidAt.split('-').map(Number); return new Date(y, m - 1, d).toISOString() })()
    : new Date().toISOString()
  const { error } = await supabase
    .from('financial_entries')
    .update({ status: 'paid', paid_at: paidAtValue })
    .eq('id', id)
  if (error) throw error
}

export async function undoPayment(id: string, userRole?: Role): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canEditEntries')
  }
  
  // Call the backend RPC which handles all logic atomically:
  // - Checks for remainder children
  // - Restores original amount if needed
  // - Deletes children
  // - Updates status
  const { error } = await supabase.rpc('undo_payment', {
    p_entry_id: id
  })
  
  if (error) throw error
}

export async function partialMarkAsPaid(
  entry: FinancialEntry,
  partialAmount: number,
  paidAt?: string,
  userRole?: Role
): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canEditEntries')
  }
  const remainder = entry.amount - partialAmount
  const paidAtValue = paidAt
    ? (() => { const [y, m, d] = paidAt.split('-').map(Number); return new Date(y, m - 1, d).toISOString() })()
    : new Date().toISOString()

  // If this entry is the origin of a recurrence template, ensure the template
  // stores the original amount before we reduce the entry's amount. The recurrence
  // should generate future occurrences with the ORIGINAL FULL AMOUNT, not affected
  // by partial payments. Set origin_amount to the full value BEFORE reducing entry.amount.
  try {
    const { error: recurrenceError } = await supabase
      .from('financial_entry_recurrences')
      .update({ origin_amount: entry.amount })
      .eq('origin_entry_id', entry.id)
    if (recurrenceError) {
      // not fatal; log and continue
      console.warn('[partialMarkAsPaid] failed to set origin_amount on recurrence', recurrenceError)
    }
  } catch (err) {
    console.warn('[partialMarkAsPaid] error while setting origin_amount on recurrence', err)
  }

  const { error: updateError } = await supabase
    .from('financial_entries')
    .update({ amount: partialAmount, status: 'paid', paid_at: paidAtValue })
    .eq('id', entry.id)
  if (updateError) throw updateError

  const { data: createdChild, error: createError } = await supabase
    .from('financial_entries')
    .insert({
      unit_id:                 entry.unit_id,
      parent_id:               entry.id,
      entry_type:              entry.entry_type,
      description:             entry.description,
      amount:                  remainder,
      due_date:                entry.due_date,
      status:                  'open',
      party_id:                entry.party_id,
      party_name_snapshot:     entry.party_name_snapshot,
      party_whatsapp_snapshot: entry.party_whatsapp_snapshot,
      payment_method_snapshot: entry.payment_method_snapshot,
      notes:                   entry.notes,
      // Preserve notification settings on the residual open parcel so alerts behave the same
      notify_on_due:           entry.notify_on_due ?? false,
      notify_time:             entry.notify_time ?? null,
      notify_offset_minutes:   entry.notify_offset_minutes ?? null,
    })
    .select()
    .single()
  if (createError) throw createError
}

export async function cancelEntry(id: string, userRole?: Role): Promise<void> {
  if (userRole) {
    await validateCapability(userRole, 'canDeleteEntries')
  }
  const { error } = await supabase
    .from('financial_entries')
    .update({ status: 'canceled' })
    .eq('id', id)
  if (error) throw error
}

export async function replicateEntry(entry: FinancialEntry): Promise<FinancialEntry> {
  // Creates a new independent entry with the same data as the source
  // Does not copy id, parent_id (clean start), created_at, updated_at
  const { data, error } = await supabase
    .from('financial_entries')
    .insert({
      unit_id:                 entry.unit_id,
      entry_type:              entry.entry_type,
      description:             entry.description,
      amount:                  entry.amount,
      due_date:                entry.due_date,
      status:                  'open',
      party_id:                entry.party_id,
      party_name_snapshot:     entry.party_name_snapshot,
      party_whatsapp_snapshot: entry.party_whatsapp_snapshot,
      payment_method_snapshot: entry.payment_method_snapshot,
      payment_method_details_snapshot: entry.payment_method_details_snapshot,
      notes:                   entry.notes,
    })
    .select()
    .single()
  if (error) throw error
  return data as FinancialEntry
}

export async function replicateEntryBatch(
  entry: FinancialEntry,
  occurrences: Array<{ due_date: string; description: string }>,
): Promise<void> {
  if (occurrences.length === 0) return

  // Reuse existing recurrence_id or generate a new one for this chain
  const recurrenceId = entry.recurrence_id ?? crypto.randomUUID()

  // Stamp the source entry if it doesn't already belong to a chain
  if (!entry.recurrence_id) {
    await supabase
      .from('financial_entries')
      .update({ recurrence_id: recurrenceId })
      .eq('id', entry.id)
  }

  const rows = occurrences.map(occ => ({
    unit_id:                         entry.unit_id,
    recurrence_id:                   recurrenceId,
    entry_type:                      entry.entry_type,
    description:                     occ.description,
    amount:                          entry.amount,
    due_date:                        occ.due_date,
    status:                          'open' as const,
    party_id:                        entry.party_id,
    party_name_snapshot:             entry.party_name_snapshot,
    party_whatsapp_snapshot:         entry.party_whatsapp_snapshot,
    payment_method_snapshot:         entry.payment_method_snapshot,
    payment_method_details_snapshot: entry.payment_method_details_snapshot,
    notes:                           entry.notes,
  }))
  const { error } = await supabase.from('financial_entries').insert(rows)
  if (error) throw error
}

/** Fetch a single entry by id (used to inspect recurrence_id before delete). */
export async function fetchEntry(id: string): Promise<FinancialEntry | null> {
  const { data, error } = await supabase
    .from('financial_entries')
    .select('*')
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  const entry = data as FinancialEntry | null
  if (!entry) return null

  // If the entry references a party, fetch the current pix_key from parties
  try {
    if (entry.party_id) {
      const { data: partyData, error: partyError } = await supabase
        .from('parties')
        .select('id, pix_key')
        .eq('id', entry.party_id)
        .maybeSingle()
      if (!partyError && partyData) {
        entry.party_pix_key_snapshot = partyData.pix_key ?? entry.party_pix_key_snapshot
      }
    }
  } catch (err) {
    console.warn('[fetchEntry] failed to fetch party pix_key:', err)
  }

  return entry
}
